import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  failSoundEmissionExecutionData,
  failSoundEmissionExecutions,
  loadSoundEmissionExecutionData,
  loadSoundEmissionExecutions,
  resetSoundEmission,
  setSoundEmissionExecutionData,
  setSoundEmissionExecutions,
} from "../redux/soundEmission";
import {
  getSoundEmissionExecutionData,
  getSoundEmissionExecutions,
} from "../services/soundEmissionApi";

export const useSoundEmission = (projectId, enabled) => {
  const dispatch = useDispatch();
  const soundEmission = useSelector((state) => state.soundEmission);
  const executionsRequestId = useRef(0);
  const dataRequestId = useRef(0);

  useEffect(() => {
    if (!enabled || !projectId) {
      executionsRequestId.current += 1;
      dataRequestId.current += 1;
      dispatch(resetSoundEmission());
      return;
    }

    const requestId = ++executionsRequestId.current;
    dataRequestId.current += 1;
    dispatch(loadSoundEmissionExecutions());

    getSoundEmissionExecutions(projectId)
      .then((executions) => {
        if (requestId !== executionsRequestId.current) return;
        dispatch(setSoundEmissionExecutions(executions));
      })
      .catch(() => {
        if (requestId !== executionsRequestId.current) return;
        dispatch(
          failSoundEmissionExecutions(
            "Could not load sound emission items.",
          ),
        );
      });

    return () => {
      executionsRequestId.current += 1;
      dataRequestId.current += 1;
    };
  }, [dispatch, enabled, projectId]);

  const selectExecution = useCallback(
    async (batchId) => {
      if (!enabled || !projectId) return;

      const requestId = ++dataRequestId.current;
      dispatch(loadSoundEmissionExecutionData(batchId));

      try {
        const data = await getSoundEmissionExecutionData(projectId, batchId);
        if (requestId !== dataRequestId.current) return;
        dispatch(setSoundEmissionExecutionData(data));
      } catch {
        if (requestId !== dataRequestId.current) return;
        dispatch(
          failSoundEmissionExecutionData(
            "Could not load sound emission data.",
          ),
        );
      }
    },
    [dispatch, enabled, projectId],
  );

  return {
    ...soundEmission,
    selectExecution,
  };
};
